import type { ColorSpec } from './types';

/**
 * Forest color spec — uses the `forest` Tailwind palette.
 *
 * Modeled on the blue spec: deep filled bg (#2d5e3f) with white text.
 * Soft and outline variants use the same light/dark pattern as other
 * "dark filled" colors (red, green, blue, purple).
 */
export const forest: ColorSpec = {
  filled: {
    border: 'border-forest-700 dark:border-forest-500',
    bg: 'bg-forest-600 text-white dark:bg-forest-700',
    bgActive: 'bg-forest-800 text-white',
    hover: 'hover:bg-forest-700 hover:text-white active:bg-forest-800 active:text-white',
    hoverDark: 'dark:hover:bg-forest-800 dark:hover:text-white dark:active:bg-forest-900 dark:active:text-white',
  },
  outline: {
    border: 'border-forest-400 dark:border-forest-500',
    bg: 'bg-forest-50 dark:bg-forest-950 text-forest-700 dark:text-forest-200',
    bgActive: 'bg-forest-600 dark:bg-forest-500 text-white',
    hover: 'hover:bg-forest-100 hover:text-forest-800 active:bg-forest-200 active:text-forest-800',
    hoverDark:
      'dark:hover:bg-forest-900 dark:hover:text-forest-100 dark:active:bg-forest-800 dark:active:text-forest-100',
    hoverWhenActive: 'hover:bg-forest-700 dark:hover:bg-forest-600',
  },
  soft: {
    border: 'border-forest-100 dark:border-forest-900',
    bg: 'bg-forest-50 dark:bg-forest-950 text-forest-700 dark:text-forest-300 font-normal',
    bgActive: 'bg-forest-600 dark:bg-forest-500 text-white',
    hover: 'hover:bg-forest-600 hover:text-white active:bg-forest-700 active:text-white',
    hoverDark: 'dark:hover:bg-forest-500 dark:hover:text-white dark:active:bg-forest-600 dark:active:text-white',
    hoverWhenActive: 'hover:bg-forest-700 dark:hover:bg-forest-600',
  },
  textFilled: {
    text: 'text-forest-600 dark:text-forest-400',
    activeBg: 'bg-forest-600 text-white dark:bg-forest-700',
    hover: 'hover:bg-forest-600 hover:text-white active:bg-forest-700',
    hoverDark: 'dark:hover:bg-forest-700 dark:hover:text-white dark:active:bg-forest-800',
  },
  textSoft: {
    text: 'text-forest-700 dark:text-forest-300 font-normal',
    activeBg: 'bg-forest-600 text-white dark:bg-forest-500',
    hover: 'hover:bg-forest-100 hover:text-forest-900 active:bg-forest-200',
    hoverDark: 'dark:hover:bg-forest-900 dark:hover:text-forest-100 dark:active:bg-forest-800',
  },
  tooltip: 'bg-forest-600 dark:bg-forest-700 text-white',
  badge: {
    filled: 'bg-forest-600 dark:bg-forest-700 text-white',
    soft: 'bg-forest-400 dark:bg-forest-500 text-white',
  },
  softIcon: 'text-forest-700 dark:text-forest-200 group-hover/btn:text-current',
  underline: {
    filled:
      '[&_u]:text-forest-200 dark:[&_u]:text-forest-300 [&_u]:decoration-forest-300 dark:[&_u]:decoration-forest-400',
    outline:
      '[&_u]:text-forest-800 dark:[&_u]:text-forest-300 [&_u]:decoration-forest-600 dark:[&_u]:decoration-forest-400',
    soft: '[&_u]:text-forest-800 dark:[&_u]:text-forest-300 [&_u]:decoration-forest-300 dark:[&_u]:decoration-forest-400',
  },
  input: {
    filled: {
      border: 'border-forest-700 dark:border-forest-500',
      bg: 'bg-forest-600 dark:bg-forest-700',
      focusBg: 'focus-within:bg-forest-700 dark:focus-within:bg-forest-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-forest-700 dark:focus-within:outline-forest-500',
      hoverActive:
        'hover:bg-forest-700 hover:text-white active:bg-forest-800 active:text-white dark:hover:bg-forest-800 dark:active:bg-forest-900',
      readOnlyBg: 'bg-forest-500 dark:bg-forest-800',
      text: 'text-white',
    },
    outline: {
      border: 'border-forest-400 dark:border-forest-500',
      bg: 'bg-forest-50 dark:bg-forest-950',
      focusBg: 'focus-within:bg-forest-100 dark:focus-within:bg-forest-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-forest-400 dark:focus-within:outline-forest-500',
      hoverActive:
        'hover:bg-forest-100 hover:text-forest-800 active:bg-forest-200 active:text-forest-800 dark:hover:bg-forest-900 dark:hover:text-forest-100 dark:active:bg-forest-800 dark:active:text-forest-100',
      readOnlyBg: 'bg-forest-50 dark:bg-forest-900',
      text: 'text-forest-700 dark:text-forest-200',
    },
    soft: {
      border: 'border-forest-100 dark:border-forest-900',
      bg: 'bg-forest-50 dark:bg-forest-950',
      focusBg: 'focus-within:bg-forest-100 dark:focus-within:bg-forest-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-forest-100 dark:focus-within:outline-forest-900',
      hoverActive:
        'hover:bg-forest-100 hover:text-forest-900 active:bg-forest-200 active:text-forest-950 dark:hover:bg-forest-900 dark:hover:text-forest-100 dark:active:bg-forest-800 dark:active:text-forest-100',
      readOnlyBg: 'bg-forest-50 dark:bg-forest-900',
      text: 'text-forest-700 dark:text-forest-300',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-forest-300 dark:text-forest-600',
    soft: 'text-forest-300 dark:text-forest-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-forest-300 dark:placeholder:text-forest-600',
    soft: 'placeholder:text-forest-300 dark:placeholder:text-forest-600',
  },
  group: {
    filled: 'border-forest-300 dark:border-forest-700 bg-forest-50 dark:bg-forest-950',
    outline: 'border-forest-400 dark:border-forest-500 bg-forest-50 dark:bg-forest-950',
    soft: 'border-forest-200 dark:border-forest-800 bg-forest-100 dark:bg-forest-900',
  },
  groupDivider: {
    filled: 'bg-forest-300 dark:bg-forest-700',
    outline: 'bg-forest-300 dark:bg-forest-600',
    soft: 'bg-forest-200 dark:bg-forest-700',
  },
  segment: {
    filled: 'border-forest-300 dark:border-forest-700 bg-forest-50 dark:bg-forest-950',
    outline: 'border-forest-400 dark:border-forest-500 bg-forest-50 dark:bg-forest-950',
  },
  segmentDivider: {
    filled: 'bg-forest-300 dark:bg-forest-700',
    outline: 'bg-forest-300 dark:bg-forest-600',
  },
  popoverHighlight: 'bg-forest-600 text-white',
  popoverText: 'text-forest-700 dark:text-forest-200',
  popoverBorder: 'border-forest-200 dark:border-forest-700',
};
